import React, { useState } from "react";
import styled from "styled-components";
import { ChevronDown, ChevronUp } from "./icons";

export type Column<T> = {
  key: keyof T;
  header: string;
  rowAlign?: "left" | "center" | "right";
  render?: (value: any, row: T) => React.ReactNode;
};

type TableProps<T> = {
  goDetailPage: (id: string, data: any) => void;
  columns: Column<T>[];
  data: T[];
};

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  border: 1px solid #ccc;
  /* 모바일*/
  @media (max-width: 767px) {
    border-radius: 4px;
  }

  /* 태블릿*/
  @media (min-width: 768px) and (max-width: 1199px) {
    border-radius: 6px;
  }

  /* PC */
  @media (min-width: 1200px) {
    border-radius: 10px;
  }
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  background-color: #f9f9f9;
  font-weight: 600;
  border-bottom: 1px solid #ddd;
  text-align: center;
  cursor: pointer;
  /* 모바일*/
  @media (max-width: 767px) {
    font-size: 12px;
    padding: 8px 4px;
  }

  /* 태블릿*/
  @media (min-width: 768px) and (max-width: 1199px) {
    font-size: 16px;
    padding: 10px;
  }

  /* PC */
  @media (min-width: 1200px) {
    font-size: 20px;
    padding: 12px;
  }
`;

const Td = styled.td<{ $align?: "left" | "center" | "right" }>`
  padding: 10px 2px;
  border-bottom: 1px solid #eee;

  font-weight: 400;
  text-align: ${({ $align }) => $align || "center"};
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  /* 모바일*/
  @media (max-width: 767px) {
    font-size: 12px;
    padding: 8px 4px;
  }

  /* 태블릿*/
  @media (min-width: 768px) and (max-width: 1199px) {
    font-size: 16px;
    padding: 12px 4px;
  }

  /* PC */
  @media (min-width: 1200px) {
    font-size: 20px;
    padding: 20px 4px;
  }
`;

const TdTextWrapper = styled.div<{ $align?: "left" | "center" | "right" }>`
  width: 100%;
  text-align: ${({ $align }) => $align || "center"};
`;

const Tr = styled.tr`
  &:nth-child(even) {
    background-color: #f5f5f5;
  }
`;

const ThContent = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`;

const SortIcon = styled.span`
  width: 16px; // 아이콘 크기 고정
  text-align: center;
  display: inline-block;
`;

export default function TableComponent<T extends Record<string, any>>({
  goDetailPage,
  columns,
  data,
}: TableProps<T>) {
  const [sortConfig, setSortConfig] = useState<{
    key: keyof T;
    direction: "asc" | "desc";
  } | null>(null);

  const handleSort = (key: keyof T) => {
    setSortConfig((prev) => {
      if (prev?.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: "asc" };
    });
  };

  const sortedData = React.useMemo(() => {
    if (!sortConfig) return data;

    const sorted = [...data].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      return sortConfig.direction === "asc"
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });

    return sorted;
  }, [data, sortConfig]);

  return (
    <TableWrapper>
      <StyledTable>
        <thead>
          <tr>
            {columns.map((col) => (
              <Th key={String(col.key)} onClick={() => handleSort(col.key)}>
                <ThContent>
                  {col.header}
                  <SortIcon>
                    {sortConfig?.key === col.key ? (
                      sortConfig.direction === "asc" ? (
                        <ChevronUp />
                      ) : (
                        <ChevronDown />
                      )
                    ) : (
                      <span style={{ visibility: "hidden" }}>
                        <ChevronUp />
                      </span>
                    )}
                  </SortIcon>
                </ThContent>
              </Th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedData.map((row, rowIdx) => (
            <Tr key={rowIdx}>
              {columns.map((col) => (
                <Td
                  key={String(col.key)}
                  $align={col.rowAlign || "center"}
                  title={String(row[col.key] ?? "")}
                  onClick={() => goDetailPage(row.id, row)}
                >
                  <TdTextWrapper $align={col.rowAlign || "center"}>
                    {col.render
                      ? col.render(row[col.key], row)
                      : String(row[col.key] ?? "")}
                  </TdTextWrapper>
                </Td>
              ))}
            </Tr>
          ))}
        </tbody>
      </StyledTable>
    </TableWrapper>
  );
}
